import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ClipboardList, CheckCircle, XCircle, Clock, Calendar, ChevronLeft, MessageSquare,
} from 'lucide-react'
import { useStore } from '@/contexts/StoreContext'
import { useTheme } from '@/contexts/ThemeContext'
import { formService } from '@/lib/services/form.service'
import PageHero from '@/components/common/PageHero'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Caixa } from '@/construtor/caixa'

const statusConfig = {
  PENDING:  { label: 'Pendente',  icon: Clock,        className: 'bg-warning/10 text-warning border-warning/30' },
  APPROVED: { label: 'Aprovado',  icon: CheckCircle,  className: 'bg-success/10 text-success border-success/30' },
  REJECTED: { label: 'Recusado',  icon: XCircle,      className: 'bg-destructive/10 text-destructive border-destructive/30' },
}

interface SubmissionDetail {
  id: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  observation: string | null
  reviewedAt: string | null
  submittedAt: string
  answers: Array<{ question: string; questionType: string; answer: string[] }>
}

export default function MySubmissionDetailPage() {
  const { submissionId } = useParams<{ submissionId: string }>()
  const navigate = useNavigate()
  const { store } = useStore()
  const { config } = useTheme()
  const [detail, setDetail] = useState<SubmissionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (store?.id && submissionId) loadDetail()
  }, [store, submissionId])

  async function loadDetail() {
    if (!store?.id || !submissionId) return
    try {
      setLoading(true)
      const data = await formService.getMySubmissionDetail(store.id, submissionId)
      setDetail(data)
    } catch (err: any) {
      setError(err?.message || 'Resposta não encontrada')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div>
        <PageHero
          title="Minhas Respostas"
          breadcrumbs={[{ label: 'Formulários', href: '/formularios' }, { label: 'Detalhes' }]}
          
        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16 flex justify-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div>
        <PageHero
          title="Minhas Respostas"
          breadcrumbs={[{ label: 'Formulários', href: '/formularios' }, { label: 'Detalhes' }]}
          
        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
          <Card className="border-border max-w-lg mx-auto">
            <CardContent className="p-12 flex flex-col items-center text-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-destructive/10 flex items-center justify-center">
                <ClipboardList className="w-10 h-10 text-destructive" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground mb-2">Resposta não encontrada</h3>
                <p className="text-muted-foreground">{error}</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/formularios')}>Voltar</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const cfg = statusConfig[detail.status] ?? statusConfig.PENDING
  const StatusIcon = cfg.icon

  return (
    <div>
      <PageHero
        title="Minhas Respostas"
        breadcrumbs={[{ label: 'Formulários', href: '/formularios' }, { label: 'Detalhes' }]}
        
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <Caixa id="formularios.resposta.voltar">
        <Button
          variant="ghost"
          className="mb-6 -ml-2 text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/formularios')}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Voltar aos formulários
        </Button>
        </Caixa>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

          {/* LEFT SIDEBAR */}
          <div className="space-y-4">
            {/* Submission info */}
            <Caixa id="formularios.resposta.info" miolo>
            <Card className="border-border">
              <CardContent className="p-6 space-y-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-foreground leading-tight mb-3">
                    Detalhes da Resposta
                  </h1>
                  <Badge className={`border text-sm px-3 py-1 ${cfg.className}`}>
                    <StatusIcon className="w-4 h-4 mr-1.5" />
                    {cfg.label}
                  </Badge>
                </div>
                <div className="space-y-2 pt-2 border-t border-border">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                      Respondido{' '}
                      {formatDistanceToNow(new Date(detail.submittedAt), { addSuffix: true, locale: ptBR })}
                    </span>
                  </div>
                  {detail.reviewedAt && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        Avaliado{' '}
                        {formatDistanceToNow(new Date(detail.reviewedAt), { addSuffix: true, locale: ptBR })}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            </Caixa>

            {/* Progress: answered count */}
            <Caixa id="formularios.resposta.progresso" miolo>
            <Card className="border-border">
              <CardContent className="p-6 space-y-3">
                <p className="text-sm font-medium text-foreground">Respostas</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{detail.answers.length} pergunta{detail.answers.length !== 1 ? 's' : ''} respondida{detail.answers.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full w-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
            </Caixa>

            {/* Observation */}
            {detail.observation && (
              <Caixa id="formularios.resposta.observacao" miolo>
              <Card className={`border ${cfg.className}`}>
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 flex-shrink-0" />
                    <p className="text-sm font-semibold">Observação do avaliador</p>
                  </div>
                  <p className="text-sm opacity-90 leading-relaxed">{detail.observation}</p>
                </CardContent>
              </Card>
              </Caixa>
            )}
          </div>

          {/* RIGHT — ANSWERS */}
          <Caixa id="formularios.resposta.respostas">
          <div className="lg:col-span-2 space-y-4">
            {detail.answers.map((a, index) => (
              <Caixa id="formularios.resposta.item" repetida key={index}>
              <Card key={index} className="border-border">
                <CardContent className="p-6">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <Label className="text-base font-medium text-foreground leading-snug">
                      {a.question}
                    </Label>
                  </div>
                  <div className="pl-10">
                    {a.answer.length === 0 ? (
                      <p className="text-muted-foreground text-sm italic">Sem resposta</p>
                    ) : (a.questionType === 'SINGLE_CHOICE' || a.questionType === 'MULTIPLE_CHOICE') ? (
                      <div className="space-y-2">
                        {a.answer.map((ans, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 p-3 rounded-lg border border-primary bg-primary/5"
                          >
                            <div className="w-4 h-4 rounded-full border-2 border-primary bg-primary flex items-center justify-center flex-shrink-0">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />
                            </div>
                            <span className="text-sm text-foreground">{ans}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="w-full rounded-md border border-border bg-background/50 px-3 py-2 text-sm text-foreground min-h-[38px] whitespace-pre-wrap">
                        {a.answer[0]}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              </Caixa>
            ))}
          </div>
          </Caixa>
        </div>
      </div>
    </div>
  )
}
